import { MAX_DESCRIPTION_LENGTH, MAX_SERIALS_PER_OUTWARD } from "../config/saleKinds";
import type { SaleKindConfig } from "../config/saleKinds";
import { createSelectableDeviceChecker, hasExceededLine } from "../state/selectors";
import type {
  SaleOrderLineView,
  SaleOutwardError,
  SaleOutwardHeader,
  ScannedSerialRow,
  SubmitStatus,
} from "../types";

export interface ValidateSaleOutwardInput {
  status: SubmitStatus;
  header: SaleOutwardHeader;
  rows: ScannedSerialRow[];
  lines: SaleOrderLineView[];
  kind: SaleKindConfig;
  /** Trang chi tiết (chỉ xem) */
  readOnly: boolean;
}

/**
 * Kiểm tra trước khi lưu / hoàn thành. Trả về toàn bộ lỗi chặn để hiển thị một lần,
 * thay vì dừng ở lỗi đầu tiên như trang cũ. Nút bấm và phím tắt cùng gọi hàm này.
 */
export const validateSaleOutward = ({
  status,
  header,
  rows,
  lines,
  kind,
  readOnly,
}: ValidateSaleOutwardInput): SaleOutwardError[] => {
  const errors: SaleOutwardError[] = [];

  if (readOnly) {
    errors.push({ code: "READ_ONLY", message: "Phiếu đang ở chế độ xem, không thể lưu." });
    return errors;
  }
  if (header.status === "LOCK") {
    errors.push({ code: "LOCKED", message: "Phiếu xuất kho đã được khoá." });
    return errors;
  }
  if (!header.sourceCode) {
    errors.push({ code: "NO_SOURCE", message: "Chưa có số chứng từ." });
  }
  if (rows.length === 0) {
    errors.push({ code: "NO_ROWS", message: "Chưa có serial nào để xuất." });
  }
  if (rows.length > MAX_SERIALS_PER_OUTWARD) {
    errors.push({
      code: "TOO_MANY_ROWS",
      message: `Số lượng serial vượt quá ${MAX_SERIALS_PER_OUTWARD}, vui lòng kiểm tra lại.`,
    });
  }

  const invalidRows = rows.filter((row) => row.warning);
  if (invalidRows.length) {
    errors.push({
      code: "INVALID_ROWS",
      message: `Có sản phẩm không hợp lệ: ${invalidRows.map((row) => row.serial).join(", ")}`,
      serial: invalidRows[0].serial?.trim() || undefined,
    });
  }

  if (hasExceededLine(lines)) {
    const exceeded = lines.find((line) => line.scannedQuantity > (line.primaryQuantity ?? 0));
    errors.push({
      code: "EXCEEDED",
      message: "Có sản phẩm vượt quá số lượng cần xuất.",
      itemCode: exceeded?.itemCode || undefined,
    });
  }

  if ((header.description?.length ?? 0) > MAX_DESCRIPTION_LENGTH) {
    errors.push({
      code: "DESCRIPTION_TOO_LONG",
      message: `Ghi chú phiếu xuất không quá ${MAX_DESCRIPTION_LENGTH} ký tự.`,
    });
  }

  if (status === "LOCK" && kind.lockRequiresNoVariance) {
    // Mỗi mã hàng lệch là một vướng mắc riêng, để "Tới dòng đơn" đúng dòng
    lines.forEach((line) => {
      const orderQuantity = line.primaryQuantity ?? 0;
      const variance = line.scannedQuantity - orderQuantity;
      if (variance === 0) return;
      errors.push({
        code: "VARIANCE",
        message: "Số lượng quét chưa khớp đơn hàng",
        detail: `${line.itemName || line.itemCode} · đã quét ${line.scannedQuantity} / ${orderQuantity}, ${
          variance < 0 ? `thiếu ${-variance}` : `thừa ${variance}`
        }`,
        itemCode: line.itemCode || undefined,
      });
    });
  }

  if (status === "LOCK") {
    // Mỗi thẻ BHMR luôn phải gắn một thiết bị trước khi hoàn thành (quyền lợi bảo hành bắt đầu từ lúc khoá).
    // Bộ kiểm tra máy chọn được dựng một lần khi gặp thẻ chưa gắn đầu tiên: O(n) thay vì O(số thẻ chưa gắn × n)
    let hasSelectableDevice: ((card: ScannedSerialRow) => boolean) | undefined;
    rows.forEach((row, index) => {
      if (!row.isWarrantyCard || (row.linkedDeviceSerial ?? "").trim()) return;
      const check = hasSelectableDevice ?? (hasSelectableDevice = createSelectableDeviceChecker(rows));
      const serial = (row.serial ?? "").trim();
      const noDevice = !check(row);
      errors.push({
        code: "CARD_NOT_LINKED",
        message: "Thẻ BHMR chưa gắn thiết bị",
        detail: `Dòng ${index + 1} · ${serial} · ${row.itemName ?? ""}${
          noDevice ? " · trên phiếu không còn máy chọn được, cần quét thêm máy hoặc bỏ thẻ" : ""
        }`,
        serial,
      });
    });
  }

  return errors;
};
